import { InputHTMLAttributes, useId, useState } from 'react';
import { useTheme } from '../../theme';

export type InputType =
  | 'text'
  | 'email'
  | 'number'
  | 'password'
  | 'tel'
  | 'url'
  | 'search';

export interface InputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type'
> {
  /** Título exibido acima do campo */
  titulo: string;
  /** Conteúdo opcional ao lado do label (ex.: ícone de tooltip) */
  labelSuffix?: React.ReactNode;
  /** Conteúdo opcional dentro do input, à direita (ex.: ícone de tooltip) */
  endAdornment?: React.ReactNode;
  /** Placeholder do input */
  placeholder?: string;
  /** Tipo do valor (text, email, number, etc.) */
  type?: InputType;
  /** Máximo de caracteres permitidos */
  maximoCaracteres?: number;
  /** Exibe botão de mostrar/ocultar senha quando type="password" */
  showPasswordToggle?: boolean;
  /** Aria-label do botão "mostrar senha" (acessibilidade) */
  passwordToggleAriaLabelShow?: string;
  /** Aria-label do botão "ocultar senha" (acessibilidade) */
  passwordToggleAriaLabelHide?: string;
  /**
   * Função de validação. Recebe o valor atual e retorna mensagem de erro
   * ou undefined se válido.
   */
  validacao?: (value: string) => string | undefined;
  /**
   * Erro externo (ex.: do React Hook Form). Quando definido, exibe a mensagem
   * e aplica borda vermelha no input.
   */
  error?: string;
}

const EyeIcon = ({ show }: { show: boolean }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    {show ? (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ) : (
      <>
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </>
    )}
  </svg>
);

export default function Input({
  titulo,
  labelSuffix,
  endAdornment,
  placeholder,
  type = 'text',
  maximoCaracteres,
  showPasswordToggle = false,
  passwordToggleAriaLabelShow = 'Mostrar senha',
  passwordToggleAriaLabelHide = 'Ocultar senha',
  validacao,
  error: errorProp,
  id: idProp,
  value: valueProp,
  defaultValue,
  onChange,
  onBlur,
  ...rest
}: InputProps) {
  const theme = useTheme();
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const isControlled = valueProp !== undefined;
  const isPassword = type === 'password';
  const [passwordVisible, setPasswordVisible] = useState(false);
  const effectiveType = isPassword && showPasswordToggle && passwordVisible ? 'text' : type;

  const [internalValue, setInternalValue] = useState(
    () => (defaultValue as string) ?? ''
  );
  const [touched, setTouched] = useState(false);
  const [internalError, setInternalError] = useState<string | undefined>();

  const value = isControlled ? (valueProp as string) : internalValue;
  const error = errorProp ?? internalError;

  const labelStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
    fontWeight: theme.typography.fontWeight.medium,
    color: theme.palette.text.primary,
    fontSize: theme.typography.fontSize.base,
    marginBottom: theme.spacing.sm,
    fontFamily: theme.typography.fontFamily,
  };

  const inputWrapperStyle: React.CSSProperties = {
    position: 'relative',
    width: '100%',
  };

  const toggleButtonStyle: React.CSSProperties = {
    position: 'absolute',
    right: theme.spacing.sm,
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    padding: theme.spacing.xs,
    cursor: 'pointer',
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: `${theme.spacing.md} ${theme.spacing.md}`,
    fontSize: theme.typography.fontSize.base,
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.input.text,
    backgroundColor: theme.palette.input.background,
    border: `${theme.shape.borderWidth.thin} solid ${theme.palette.border.input}`,
    borderRadius: theme.shape.borderRadius.md,
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s, box-shadow 0.2s',
  };

  const errorStyle: React.CSSProperties = {
    fontSize: theme.typography.fontSize.sm,
    color: theme.palette.error.main,
    marginTop: theme.spacing.xs,
  };

  const validate = (val: string) => {
    if (!validacao) return undefined;
    return validacao(val);
  };

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    const newValue = e.target.value;
    if (!isControlled) setInternalValue(newValue);
    if (touched) setInternalError(validate(newValue));
    onChange?.(e);
  };

  const handleBlur: React.FocusEventHandler<HTMLInputElement> = e => {
    setTouched(true);
    setInternalError(validate(value));
    onBlur?.(e);
  };

  const inputProps = {
    ...rest,
    id,
    type: effectiveType,
    value,
    placeholder,
    maxLength: maximoCaracteres,
    onChange: handleChange,
    onBlur: handleBlur,
    style: {
      ...inputStyle,
      ...(isPassword && showPasswordToggle ? { paddingRight: 40 } : {}),
      ...(endAdornment ? { paddingRight: 40 } : {}),
      ...(error
        ? {
            borderColor: theme.palette.error.main,
            borderWidth: theme.shape.borderWidth.thin,
          }
        : {}),
    },
    'aria-invalid': !!error,
    'aria-describedby': error ? `${id}-error` : undefined,
  };

  return (
    <div
      className="e-learning-input-wrapper"
      style={{ marginBottom: theme.spacing.md }}
    >
      <label htmlFor={id} style={labelStyle}>
        {titulo}
        {labelSuffix}
      </label>
      <div style={inputWrapperStyle}>
        <input className="e-learning-input" {...inputProps} />
        {endAdornment && (
          <span style={toggleButtonStyle}>{endAdornment}</span>
        )}
        {isPassword && showPasswordToggle && (
          <button
            type="button"
            style={toggleButtonStyle}
            onClick={() => setPasswordVisible(prev => !prev)}
            tabIndex={-1}
            aria-label={passwordVisible ? passwordToggleAriaLabelHide : passwordToggleAriaLabelShow}
          >
            <EyeIcon show={!passwordVisible} />
          </button>
        )}
      </div>
      {error && (
        <span id={`${id}-error`} role="alert" style={errorStyle}>
          {error}
        </span>
      )}
    </div>
  );
}
